// Absence Management Utility Functions

/**
 * Formats a date string or Date object to a readable format
 */
export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return "-";
  
  const dateObj = typeof date === "string" ? new Date(date) : date;
  
  return dateObj.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Formats a date range for display
 */
export const formatDateRange = (startDate: string, endDate: string): string => {
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  
  if (start === end) {
    return start;
  }
  
  return `${start} - ${end}`;
};

/**
 * Parses a Date or date string into a local-midnight Date,
 * avoiding the UTC-midnight pitfall of `new Date("YYYY-MM-DD")`.
 */
const toLocalDate = (d: Date | string): Date => {
  if (typeof d === "string") {
    const [y, m, day] = d.split("T")[0].split("-").map(Number);
    return new Date(y, m - 1, day);
  }
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

/**
 * Calculates the total number of calendar days between two dates (including weekends)
 */
export const calculateTotalDays = (startDate: Date | string, endDate: Date | string): number => {
  const start = toLocalDate(startDate);
  const end = toLocalDate(endDate);
  if (start > end) {
    return 0;
  }
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
};

/**
 * Calculates the number of business days between two dates (excluding weekends)
 */
export const calculateBusinessDays = (startDate: Date | string, endDate: Date | string): number => {
  const currentDate = toLocalDate(startDate);
  const end = toLocalDate(endDate);

  if (currentDate > end) {
    return 0;
  }

  let businessDays = 0;

  while (currentDate <= end) {
    const dayOfWeek = currentDate.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      businessDays++;
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return businessDays;
};

/**
 * Checks if a date is a weekend
 */
export const isWeekend = (date: Date): boolean => {
  const dayOfWeek = date.getDay();
  return dayOfWeek === 0 || dayOfWeek === 6;
};

/**
 * Checks if a date is a business day
 */
export const isBusinessDay = (date: Date): boolean => {
  return !isWeekend(date);
};

/**
 * Formats business days count with proper pluralization
 */
export const formatBusinessDays = (days: number): string => {
  if (days === 1) return "1 business day";
  return `${days} business days`;
};

/**
 * Converts backend date string (ISO) to input date format (YYYY-MM-DD)
 */
export const toInputDateFormat = (dateString: string | null | undefined): string => {
  if (!dateString) return "";
  return dateString.split("T")[0];
};

/**
 * Converts input date format (YYYY-MM-DD) to a date-only ISO string
 * that won't shift in any timezone (appends T12:00:00 as noon-UTC anchor).
 */
export const toISODateString = (dateString: string): string => {
  if (!dateString) return "";
  return `${dateString.split("T")[0]}T00:00:00`;
};

/**
 * Checks if a request can be cancelled based on status and dates
 */
export const canCancelRequest = (
  status: number,
  startDate: string,
  isAdmin: boolean = false
): boolean => {
  // Can't cancel denied or already cancelled requests
  if (status === 3 || status === 4) {
    return false;
  }

  // Admin can cancel any submitted or approved request
  if (isAdmin) {
    return status === 1 || status === 2;
  }

  // Employee can cancel submitted requests
  if (status === 1) {
    return true;
  }

  // Employee can cancel approved requests only if not started yet
  if (status === 2) {
    const start = new Date(startDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return start > today;
  }

  return false;
};

/**
 * Gets a user-friendly message for why a request cannot be cancelled
 */
export const getCancelDisabledReason = (status: number, startDate: string): string => {
  if (status === 3) return "Denied requests cannot be cancelled";
  if (status === 4) return "Request is already cancelled";
  
  if (status === 2) {
    const start = new Date(startDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (start <= today) {
      return "Cannot cancel an absence that has already started";
    }
  }
  
  return "";
};







